import { useEffect, useRef } from "react";

import "./SpreadText.scss";

const RADIUS = 110;
const MAX_PUSH = 16;
const LERP = 0.12;

function SpreadText({ children, className = "" }) {
    const rootRef = useRef(null);
    const animationRef = useRef(null);

    const text = String(children ?? "");
    const words = text.trim().split(/\s+/);

    useEffect(() => {
        const root = rootRef.current;

        if (!root) return;

        const letters = Array.from(
            root.querySelectorAll(
                ".spread-text__letter"
            )
        );

        if (!letters.length) return;

        const positions = letters.map(() => ({
            current: 0,
            target: 0,
        }));

        let mouseX = null;
        let isActive = false;

        const updateTargets = () => {
            letters.forEach((letter, index) => {
                if (!isActive || mouseX === null) {
                    positions[index].target = 0;
                    return;
                }

                const rect =
                    letter.getBoundingClientRect();

                const centerX =
                    rect.left + rect.width / 2;

                const distance =
                    centerX - mouseX;

                const absoluteDistance =
                    Math.abs(distance);

                if (absoluteDistance >= RADIUS) {
                    positions[index].target = 0;
                    return;
                }

                const influence =
                    1 -
                    absoluteDistance / RADIUS;

                const easedInfluence =
                    influence * influence;

                const direction =
                    distance < 0 ? -1 : 1;

                positions[index].target =
                    direction *
                    MAX_PUSH *
                    easedInfluence;
            });
        };

        const animate = () => {
            letters.forEach((letter, index) => {
                const position =
                    positions[index];

                position.current +=
                    (
                        position.target -
                        position.current
                    ) *
                    LERP;

                if (
                    Math.abs(
                        position.target -
                        position.current
                    ) < 0.01
                ) {
                    position.current =
                        position.target;
                }

                // Միայն X առանցքով շարժում։
                // Y-ը միշտ 0 է։
                letter.style.transform =
                    `translate3d(${position.current}px, 0, 0)`;
            });

            animationRef.current =
                requestAnimationFrame(animate);
        };

        const handleMouseMove = (event) => {
            mouseX = event.clientX;
            isActive = true;

            updateTargets();
        };

        const handleMouseLeave = () => {
            isActive = false;
            mouseX = null;

            positions.forEach((position) => {
                position.target = 0;
            });
        };

        root.addEventListener(
            "mousemove",
            handleMouseMove
        );

        root.addEventListener(
            "mouseleave",
            handleMouseLeave
        );

        animationRef.current =
            requestAnimationFrame(animate);

        return () => {
            root.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            root.removeEventListener(
                "mouseleave",
                handleMouseLeave
            );

            if (animationRef.current) {
                cancelAnimationFrame(
                    animationRef.current
                );
            }
        };
    }, [text]);

    return (
        <span
            ref={rootRef}
            className={`spread-text ${className}`}
            aria-label={text}
        >
            {words.map((word, wordIndex) => (
                <span
                    key={`${word}-${wordIndex}`}
                    className="spread-text__word"
                >
                    {Array.from(word).map(
                        (character, letterIndex) => (
                            <span
                                key={`${character}-${letterIndex}`}
                                className="spread-text__letter"
                                aria-hidden="true"
                            >
                                {character}
                            </span>
                        )
                    )}
                </span>
            ))}
        </span>
    );
}

export default SpreadText;